/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 31/10/2023
 */

import { Backdrop, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import React, { useContext, useEffect } from 'react'
import styles from "./PaymentStep.module.css"
import { isMobile } from 'react-device-detect'
import CheckoutContext from '../../Context/CheckoutContext';
import Summary from './Summary';
import StripeContainer from './StripeContainer';
import { useCreateRequestMutation, useGetPaymentMethodsQuery } from '../../Store/Services/CheckoutService';
import { SET_PAYMENT_METHOD } from '../../Pages/CheckoutPage';
import { useState } from 'react';
import CheckoutSuccess from './CheckoutSuccess';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const PaymentStep = () => {

    const { checkoutData, dispatch } = useContext(CheckoutContext);
    const savedAddress = useSelector(state => state.address.savedAddress);
    const [openSuccess, setOpenSuccess] = useState(false)

    const {data: paymentMethods, isSuccess, isLoading} = useGetPaymentMethodsQuery()

    useEffect(() => {
        if(isSuccess && !isLoading){
            dispatch({ type: SET_PAYMENT_METHOD, payload: paymentMethods[0].paymentMethodId})
        }
    }, [paymentMethods])

    const onMethodClick = (id) => {
        dispatch({ type: SET_PAYMENT_METHOD, payload: id})
    }

    const [checkout, {isLoading: isConfirmLoading}] = useCreateRequestMutation();

    const handleCheckout = async () => {
        try {
            const mergedData = {
                ...checkoutData,
                addressLine1: checkoutData.addressLine1 || savedAddress?.addLine1,
                addressLine2: checkoutData.addressLine2 || savedAddress?.addLine2,
                addressLine3: checkoutData.addressLine3 || savedAddress?.addLine3,
                areaId: checkoutData.areaId || savedAddress?.areaId,
                areaName: checkoutData.areaName || savedAddress?.areaName,
                stateId: checkoutData.stateId || savedAddress?.stateId,
                stateName: checkoutData.stateName || savedAddress?.stateName,
            };
    
            const confirm = await checkout(mergedData).unwrap();
            setOpenSuccess(true);
            const isSignedIn = parseInt(sessionStorage.getItem("isSignedIn"), 10); 
            if (isSignedIn === 2) {
                sessionStorage.removeItem("access_token");
                sessionStorage.removeItem("isSignedIn");
            }
        } catch (err) {
            console.error("Failed to save the item: ", err);
            toast.error("Error, something went wrong!");
        }
    };
    

    return (
        <div className={styles.container}>
            {isLoading && (
                <Backdrop
                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={isLoading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
            <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                    <Paper className={styles.paper} elevation={isMobile ? 0 : 3}>
                        <Typography className={styles.topic}>Payment Details</Typography>
                        <div className={styles.inputContainer}>
                            <Typography className={styles.subTitle}>Payment method</Typography>
                            <div className={styles.selectionContainer}>
                                {isSuccess && paymentMethods.map(method => (
                                    <div
                                        key={method.paymentMethodId}
                                        className={checkoutData.paymentMethodId == method.paymentMethodId ?
                                            styles.optionSelected : styles.option}
                                        onClick={() => onMethodClick(method.paymentMethodId)}
                                    >
                                        Pay with {method.paymentMethod}
                                    </div>
                                ))}
                            </div>
                        </div>
                        {checkoutData.paymentMethodId === 1 ? <div className={styles.paymentContainer}>
                            <StripeContainer />
                        </div> :
                        <div className={styles.paymentContainer}>
                            <Button className={styles.button} fullWidth onClick={() => handleCheckout()} disabled={isConfirmLoading} variant='contained'>
                                Confirm
                                {isConfirmLoading && <CircularProgress size={24} className={styles.buttonProgress} />}
                            </Button>
                        </div>
                        }
                    </Paper>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Summary />
                </Grid>
            </Grid>
            <CheckoutSuccess open={openSuccess} />
        </div>
    )
}

export default PaymentStep